import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Card, Divider, Page, Spinner, Text, Badge, Banner, List, Icon } from '@shopify/polaris';
import { CheckIcon } from '@shopify/polaris-icons';
import { useLocation } from 'react-router-dom';
import PlanConfirmationModal from '../Modals/PlanConfirmationModal';
import FreePlanModal from '../Modals/FreePlanModal'; // Add this import
import BasicPlanModal from '../Modals/BasicPlanModal';
import ProPlanModal from '../Modals/ProPlanModal';

function Subscription({ shopOrigin, apiKey }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const reason = searchParams.get("reason");

  const [clickedCard, setClickedCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [chargeId, setChargeId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isFreePlanModalOpen, setIsFreePlanModalOpen] = useState(false); // Add this state
  const [isBasicPlanModalOpen, setIsBasicPlanModalOpen] = useState(false); // Add this state
  const [isProPlanModalOpen, setIsProPlanModalOpen] = useState(false); // Add this state



  const fetchSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/get_subscription/?shop_name=${shopOrigin}`);
      const data = await response.json();
      setCurrentPlan(data.plan);
      setChargeId(data.charge_id);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  }, [shopOrigin]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const staticButtonClick = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    try {
      const formData = new URLSearchParams();
      formData.append('shop_name', shopOrigin);

      const response = await fetch('/create_usage_record/', {
        method: 'POST',
        body: formData,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      const data = await response.json();

      if (data.status === 'Success') {
        // console.log('Usage record created:', data.usage_record_id);
        // Optionally, show a success message to the user
      } else {
        console.error('Error creating usage record:', data.message);
        // Optionally, show an error message to the user
      }
    } catch (error) {
      console.error('Error creating usage record:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, shopOrigin]);

  const handleCardClick = useCallback((plan) => {
    if (loading || plan === currentPlan) return;
    
    // Handle Free Plan differently
    if (plan === 'free') {
      setSelectedPlan('free');
      setIsFreePlanModalOpen(true);
      return;
    }
    if(plan === 'basic'){
      setSelectedPlan('basic');
      setIsBasicPlanModalOpen(true);
    }
    if(plan === 'pro'){
      setSelectedPlan('pro');
      setIsProPlanModalOpen(true);
    }
    
    // Handle other plans with existing modal
    // setSelectedPlan(plan);
    // setIsModalOpen(true);
  }, [loading, currentPlan]);

  
  const handleConfirmSubscription = useCallback(async () => {
    if (loading || !selectedPlan) return;
    setLoading(true);

    // Map plan names to their values
    const planValues = {
      free: '0.0',
      basic: '9.5',
      pro: '19.5',
    };

    const planValue = planValues[selectedPlan] || '0.0';

    try {
      // Handle Free Plan without Shopify billing redirect
      if (selectedPlan === 'free') {
        const formData = new URLSearchParams();
        formData.append('shop_name', shopOrigin);
        formData.append('plan', 'free');

        const response = await fetch('/activate_free_plan/', {
          method: 'POST',
          body: formData,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        });
        
        const data = await response.json();
        
        if (data.status === 'Success') {
          // Refresh the current plan state
          await fetchSubscription();
          setIsFreePlanModalOpen(false);
          setSelectedPlan(null);
        } else {
          console.error("Error activating free plan:", data.message);
        }
        setLoading(false);
        return;
      }

      // Handle paid plans (basic/pro) with Shopify billing
      const formData = new URLSearchParams();
      formData.append('shop_name', shopOrigin);
      formData.append('plan', selectedPlan);
      formData.append('planvalue', planValue);

      const response = await fetch('/billing_plan/', {
        method: 'POST',
        body: formData,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      const data = await response.json();

      if (data.url) {
        window.top.location.href = data.url;
      } else {
        console.error("No redirect URL returned:", data);
      }
    } catch (error) {
      console.error('Error processing subscription:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, selectedPlan, shopOrigin, fetchSubscription]);


  const handleCancel = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    try {
      const response = await fetch(`/cancel_billing_plan/?shop_name=${shopOrigin}&charge_id=${chargeId}`);
      const data = await response.json();
      if (data.status === 'Success') {
        const fetchResponse = await fetch(`/get_subscription/?shop_name=${shopOrigin}`);
        const fetchData = await fetchResponse.json();
        setCurrentPlan(fetchData.plan);
        setChargeId(fetchData.charge_id);
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, shopOrigin, chargeId]);

  const spinnerOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  };

  // Responsive container styles
  const containerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    justifyContent: 'center',
    alignItems: 'stretch',
  };

  // Style for custom list items with checkmarks
  const listItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    // marginBottom: '8px',
  };

  // Media query styles for different screen sizes
  const responsiveStyles = `
    .Polaris-Icon{
      margin: 0px !important;
    }
    .Polaris-ShadowBevel{
      width: 100%;
    }
    .Polaris-ShadowBevel.Card1{
      height: 100%;
    }
    .Polaris-ShadowBevel{
      // transition: border 2s ease;
      // cursor: pointer;
    }

    // .Polaris-ShadowBevel:hover {
    //   border: 1px solid black;
    // }
    @media (max-width: 768px) {
      .subscription-container {
        flex-direction: column;
        align-items: center;
      }
      .subscription-card-wrapper {
        flex: 1 1 100%;
        max-width: 100%;
        width: 100%;
      }
    }
    
    @media (min-width: 769px) and (max-width: 1024px) {
      .subscription-card-wrapper {
        flex: 1 1 calc(50% - 0.5rem);
        max-width: calc(50% - 0.5rem);
      }
    }
    
    @media (min-width: 1025px) {
      .subscription-card-wrapper {
        flex: 1 1 calc(33.333% - 0.67rem);
        max-width: calc(33.333% - 0.67rem);
      }
    }
  `;

  return (
    <>
      <style>{responsiveStyles}</style>
      {loading && (
        <div style={spinnerOverlayStyle}>
          <Spinner size="large" />
        </div>
      )}
      {!loading && (
        <Page title="Pricing">
          <div style={{ marginBottom: '15px' }}>
            {(!currentPlan || reason === 'subscription-required') && (
              <Banner title="Subscription Required" tone="warning">
                <p>Please subscribe first to create content.</p>
              </Banner>
            )}
          </div>
          {/* Flexbox Container */}
          <div className="subscription-container" style={containerStyle}>
            {/* Free Plan Card */}
            <div 
              className="subscription-card-wrapper"  
            >
              <Card
                style={{
                  flex: 1,
                  height: '100%',
                  cursor: loading || currentPlan === 'free' ? 'not-allowed' : 'pointer',
                  border: currentPlan === 'free' ? '2px solid #008060' : (clickedCard === 'free' ? '2px solid #5c6ac4' : 'none'),
                  pointerEvents: loading || currentPlan === 'free' ? 'none' : 'auto',
                  position: 'relative',
                  width: '100%',
                }}
                height="100%"
                className="Card1"
              >
                <div style={{ height: '360px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text color="subdued" variant="bodyMd" as="p">
                      Free Basic Plan
                    </Text>
                    {currentPlan === 'free' && (
                      <Badge tone="info">
                        Current Plan
                      </Badge>
                    )}
                  </div>

                  <div style={{paddingBottom:"6px"}}>
                    <Text variant="heading2xl" as="h3">
                      Free
                    </Text>
                  </div>

                  <div style={{height: "50px"}}>
                    <Text variant='bodyMd' as='p' tone='subdued' style={{ marginBottom: '8px' }}>
                      $3 per extra generated article, includes all plan features.
                    </Text>
                  </div>

                  <Divider />

                  <div style={{ marginTop: '15px' }}>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">3 AI-generated blog posts / month</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Drag-and-drop text editor</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Blog post scheduling</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Titles and text content included</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Basic analytics (Sessions & Engagement)</Text>
                    </div>
                  </div>
                  
                  {/* Show Choose Plan button only if the current plan is not 'free' */}
                  <div style={{display: "flex", justifyContent:"center",}}>
                    <div style={{ display: currentPlan === 'free' ? 'none' : 'block',  marginTop:"92px", }}>
                      <Button 
                        onClick={currentPlan !== 'free' ? () => handleCardClick('free') : null} 
                        variant="primary" 
                      >
                        Choose Plan
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>

            </div>
            {/* Basic Plan Card */}
            <div 
              className="subscription-card-wrapper" 
            >
              <Card
                style={{
                  flex: 1,
                  height: '100%',
                  cursor: loading || currentPlan === 'basic' ? 'not-allowed' : 'pointer',
                  border: currentPlan === 'basic' ? '2px solid #008060' : (clickedCard === 'basic' ? '2px solid #5c6ac4' : 'none'),
                  pointerEvents: loading || currentPlan === 'basic' ? 'none' : 'auto',
                  position: 'relative',
                  width: '100%',
                }}
              >
                <div style={{ minHeight: '360px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text color="subdued" variant="bodyMd" as="p">
                      Basic Plan
                    </Text>
                    {currentPlan === 'basic' && (
                      <Badge tone="info">
                        Current Plan
                      </Badge>
                    )}
                  </div>

                  <Text variant="heading2xl" as="h3">
                    $9.5 <span style={{ fontSize: '12px' }}>/month</span>
                  </Text>

                  <div style={{height: "50px"}}>
                    <Text variant='bodyMd' as='p' tone='subdued' style={{ marginBottom: '8px' }}>
                      $3 per extra generated article, includes all plan features.
                    </Text>
                  </div>

                  <Divider />

                  <div style={{ marginTop: '15px' }}>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">35 AI-generated blog posts / month</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Drag-and-drop text editor</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Blog post scheduling</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Titles and text content included</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">AI-generated images included</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Basic analytics (Sessions & Engagement)</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Optimize & Similar buttons enabled</Text>
                    </div>
                  </div>

                  {/* Show Choose Plan button only if the current plan is not 'basic' */}
                  <div style={{display: "flex", justifyContent:"center"}}>
                    <div style={{ display: currentPlan === 'basic' ? 'none' : 'block', marginTop:"52px"}}>
                      <Button 
                        onClick={currentPlan !== 'basic' ? () => handleCardClick('basic') : null} 
                        variant="primary" 
                      >
                        Choose Plan
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>

            </div>
            {/* Pro Plan Card */}
            <div 
              className="subscription-card-wrapper" 
            >
              <Card
                style={{
                  flex: 1,
                  height: '100%',
                  cursor: loading || currentPlan === 'pro' ? 'not-allowed' : 'pointer',
                  border: currentPlan === 'pro' ? '2px solid #008060' : (clickedCard === 'pro' ? '2px solid #5c6ac4' : 'none'),
                  pointerEvents: loading || currentPlan === 'pro' ? 'none' : 'auto',
                  position: 'relative',
                  width: '100%',
                }}
              >
                <div style={{ minHeight: '360px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text color="subdued" variant="bodyMd" as="p">
                      Pro Plan
                    </Text>
                    {currentPlan === 'pro' && (
                      <Badge tone="info">
                        Current Plan
                      </Badge>
                    )}
                  </div>

                  <Text variant="heading2xl" as="h3">
                    $19.5 <span style={{ fontSize: '12px' }}>/month</span>
                  </Text>
                  <div style={{height: "50px"}}>
                    <Text variant='bodyMd' as='p' tone='subdued' >
                      Unlimited AI-generated blog posts 
                    </Text>
                    <Text variant='bodyMd' as='p' tone='subdued' style={{ marginBottom: '8px' }}>
                      Free for 90 days, then $19.5/month 
                    </Text>
                  </div>

                  <Divider />

                  <div style={{ marginTop: '15px' }}>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Unlimited AI-generated blog posts</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">AI-generated images included</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Full analytics dashboard</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Optimize & Similar buttons enabled</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Product grids inside articles</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Text overlays on images</Text>
                    </div>
                    <div style={listItemStyle}>
                      <Icon source={CheckIcon} tone="success" />
                      <Text variant="bodyMd">Priority support (24 / 7)</Text>
                    </div>
                  </div>

                  {/* Show Choose Plan button only if the current plan is not 'pro' */}
                  <div style={{display: "flex", justifyContent:"center"}}>
                    <div style={{ display: currentPlan === 'pro' ? 'none' : 'block', marginTop:"52px" }}>
                      <Button 
                        onClick={currentPlan !== 'pro' ? () => handleCardClick('pro') : null} 
                        variant="primary" 
                      >
                        Choose Plan
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>

            </div>
          </div>
          {/* <Button onClick={staticButtonClick}>Static</Button> */}
        </Page>
      )}

      <FreePlanModal
        isOpen={isFreePlanModalOpen}
        onClose={() => setIsFreePlanModalOpen(false)}
        onConfirm={handleConfirmSubscription}
        planName={selectedPlan}
      />

      <BasicPlanModal
        isOpen={isBasicPlanModalOpen}
        onClose={() => setIsBasicPlanModalOpen(false)}
        onConfirm={handleConfirmSubscription}
        planName={selectedPlan}
      />

      <ProPlanModal
        isOpen={isProPlanModalOpen}
        onClose={() => setIsProPlanModalOpen(false)}
        onConfirm={handleConfirmSubscription}
        planName={selectedPlan}
      />

      <PlanConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmSubscription}
        planName={selectedPlan}
      />
    </>
  );
}

export default Subscription;